'use client';

import Button, { ButtonVariants } from '@pixum/button';
import Text, { TEXT_VARIANT } from '@pixum/text';
import { Dialog, DialogContent, DialogFooter, DIALOG_ROLES } from '@pixum/web-dialog';
import type { LinkNode } from 'data-layer';

interface CartDialogAlertGeneralProps {
  show: boolean;
  onClose: () => void;
  constants: Record<string, string>;
  links?: LinkNode[];
}

/**
 * General error dialog for PayPal Express failures.
 *
 * Shown when createOrder returns no merchantId, or when approveOrder returns
 * a 422 with an unrecognised error code, or when handleRegisterUser returns
 * a non-409/400 status.
 */
export function CartDialogAlertGeneral({ show, onClose, constants, links }: CartDialogAlertGeneralProps) {
  const headline = constants['i18n_sorry_error_occures'];
  const message = constants['i18n_cart_general_error_message'];
  const serviceLinkText = constants['i18n_cart_customer_service_text'];
  const contactLabel = constants['i18n_contact_customer_service'];
  const tryLaterLabel = constants['i18n_try_later'];
  const serviceUrl = links?.find(link => link.id === 'service')?.url;

  // Split message on %service% placeholder to embed a link to customer service.
  // Falls back to plain text if the placeholder is absent or the URL is missing.
  const [messagePrefix = '', messageSuffix = ''] = message?.split('%service%') ?? [];

  const handleContactService = () => {
    if (serviceUrl) {
      window.open(serviceUrl, '_blank');
    }
  };

  return (
    <Dialog
      content={
        <DialogContent>
          <>
            <Text variant={TEXT_VARIANT.HEADLINE}>{headline}</Text>
            <Text>
              {messagePrefix}
              {serviceLinkText && serviceUrl && (
                <a href={serviceUrl} rel="noreferrer" target="_blank">
                  {serviceLinkText}
                </a>
              )}
              {messageSuffix}
            </Text>
          </>
        </DialogContent>
      }
      footer={
        <DialogFooter
          left={
            <Button label={contactLabel} onClick={handleContactService} variant={ButtonVariants.TERTIARY}>
              {contactLabel}
            </Button>
          }
          right={
            <Button label={tryLaterLabel} onClick={onClose} variant={ButtonVariants.TERTIARY}>
              {tryLaterLabel}
            </Button>
          }
          role={DIALOG_ROLES.ALERT}
        />
      }
      onClose={onClose}
      open={show}
      role={DIALOG_ROLES.ALERT}
      showCloseButton={false}
    />
  );
}
